// AdminCompanyManagement.tsx
import * as React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  createCompany,
  deleteCompany,
  fetchCompanies,
  fetchEmployeeCompanyAndDepartment,
  fetchManagerViewerByAuthUserId,
  getCurrentUser,
  renameCompanyDepartment,
  updateCompanyHiddenFromRegistration,
  updateCompanyName,
} from "../api/queries";
import { useI18n } from "../i18n/I18nProvider";
import type { CompanyRow } from "../types/company";

function SectionIconBox(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2.5,
        bgcolor: "#FFFFFF",
        border: "1px solid #E5E7EB",
        display: "grid",
        placeItems: "center",
        color: "#111827",
        flexShrink: 0,
      }}
    >
      {props.children}
    </Box>
  );
}

export default function AdminCompanyManagement(props: { handlePageChange: (page: string) => void }) {
  void props;

  const { lang } = useI18n();
  const typedLang: "en" | "de" = lang === "de" ? "de" : "en";
  const L = React.useCallback((en: string, de: string) => (typedLang === "de" ? de : en), [typedLang]);

  const [loading, setLoading] = React.useState(true);
  const [uiError, setUiError] = React.useState<string | null>(null);
  const [companies, setCompanies] = React.useState<CompanyRow[]>([]);
  const [employeeCounts, setEmployeeCounts] = React.useState<Record<string, number>>({});
  const [departmentsByCompany, setDepartmentsByCompany] = React.useState<Record<string, string[]>>({});
  const [departmentCounts, setDepartmentCounts] = React.useState<Record<string, Record<string, number>>>({});

  const [addOpen, setAddOpen] = React.useState(false);
  const [addName, setAddName] = React.useState("");
  const [addSaving, setAddSaving] = React.useState(false);
  const [addError, setAddError] = React.useState<string | null>(null);

  const [renameTarget, setRenameTarget] = React.useState<CompanyRow | null>(null);
  const [renameName, setRenameName] = React.useState("");
  const [renameSaving, setRenameSaving] = React.useState(false);
  const [renameError, setRenameError] = React.useState<string | null>(null);

  const [togglingHiddenId, setTogglingHiddenId] = React.useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = React.useState<CompanyRow | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const [editingDept, setEditingDept] = React.useState<{ companyId: string; oldName: string } | null>(null);
  const [editingDeptValue, setEditingDeptValue] = React.useState("");
  const [savingDept, setSavingDept] = React.useState(false);
  const [deptError, setDeptError] = React.useState<string | null>(null);

  const [deleteDeptTarget, setDeleteDeptTarget] = React.useState<{
    companyId: string;
    name: string;
    count: number;
  } | null>(null);
  const [deleteDeptMoveTo, setDeleteDeptMoveTo] = React.useState("");
  const [deleteDeptSaving, setDeleteDeptSaving] = React.useState(false);
  const [deleteDeptError, setDeleteDeptError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setUiError(null);

    try {
      const { data: userData, error: userErr } = await getCurrentUser();
      if (userErr) throw userErr;
      if (!userData?.user) {
        throw new Error(L("Not authenticated.", "Nicht angemeldet."));
      }

      const { data: me, error: meErr } = await fetchManagerViewerByAuthUserId(userData.user.id);
      if (meErr) throw meErr;
      if (!me?.id || String(me.company_role) !== "admin") {
        throw new Error(L("You do not have admin access.", "Du hast keinen Admin-Zugriff."));
      }

      const [{ data: companyRows, error: companiesErr }, { data: employeeRows, error: employeesErr }] =
        await Promise.all([fetchCompanies(), fetchEmployeeCompanyAndDepartment()]);

      if (companiesErr) throw companiesErr;
      if (employeesErr) throw employeesErr;

      const counts: Record<string, number> = {};
      const deptCounts: Record<string, Record<string, number>> = {};

      for (const row of (employeeRows ?? []) as { company_id: string | null; department: string | null }[]) {
        if (!row.company_id) continue;
        counts[row.company_id] = (counts[row.company_id] ?? 0) + 1;

        const dept = (row.department ?? "").trim();
        if (dept) {
          if (!deptCounts[row.company_id]) deptCounts[row.company_id] = {};
          deptCounts[row.company_id][dept] = (deptCounts[row.company_id][dept] ?? 0) + 1;
        }
      }

      const depts: Record<string, string[]> = {};
      for (const [companyId, counted] of Object.entries(deptCounts)) {
        depts[companyId] = Object.keys(counted).sort((a, b) => a.localeCompare(b));
      }

      setCompanies((companyRows ?? []) as CompanyRow[]);
      setEmployeeCounts(counts);
      setDepartmentsByCompany(depts);
      setDepartmentCounts(deptCounts);
    } catch (err) {
      setUiError(
        err instanceof Error ? err.message : L("Something went wrong.", "Etwas ist schiefgelaufen.")
      );
    } finally {
      setLoading(false);
    }
  }, [L]);

  React.useEffect(() => {
    load();
  }, [load]);

  function openAddDialog() {
    setAddName("");
    setAddError(null);
    setAddOpen(true);
  }

  function closeAddDialog() {
    if (addSaving) return;
    setAddOpen(false);
  }

  async function handleCreateCompany() {
    const name = addName.trim();
    if (!name) return;

    setAddSaving(true);
    setAddError(null);

    const { data, error } = await createCompany(name);

    if (error || !data) {
      setAddError(L("Failed to create company.", "Unternehmen konnte nicht erstellt werden."));
      setAddSaving(false);
      return;
    }

    setCompanies((prev) => [...prev, data as CompanyRow].sort((a, b) => a.name.localeCompare(b.name)));
    setAddSaving(false);
    setAddOpen(false);
  }

  function openRenameDialog(company: CompanyRow) {
    setRenameTarget(company);
    setRenameName(company.name);
    setRenameError(null);
  }

  function closeRenameDialog() {
    if (renameSaving) return;
    setRenameTarget(null);
  }

  async function handleRenameCompany() {
    if (!renameTarget) return;
    const name = renameName.trim();
    if (!name) return;

    setRenameSaving(true);
    setRenameError(null);

    const { error } = await updateCompanyName(renameTarget.id, name);

    if (error) {
      setRenameError(L("Failed to save changes.", "Änderungen konnten nicht gespeichert werden."));
      setRenameSaving(false);
      return;
    }

    setCompanies((prev) =>
      prev
        .map((c) => (c.id === renameTarget.id ? { ...c, name } : c))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
    setRenameSaving(false);
    setRenameTarget(null);
  }

  async function handleToggleHiddenFromRegistration(company: CompanyRow) {
    const nextHidden = !company.hidden_from_registration;

    setTogglingHiddenId(company.id);
    setCompanies((prev) => prev.map((c) => (c.id === company.id ? { ...c, hidden_from_registration: nextHidden } : c)));

    const { error } = await updateCompanyHiddenFromRegistration(company.id, nextHidden);

    if (error) {
      // Roll back the optimistic update.
      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, hidden_from_registration: company.hidden_from_registration } : c))
      );
      setUiError(L("Failed to save changes.", "Änderungen konnten nicht gespeichert werden."));
    }

    setTogglingHiddenId(null);
  }

  function openDeleteDialog(company: CompanyRow) {
    setDeleteTarget(company);
    setDeleteError(null);
  }

  function closeDeleteDialog() {
    if (deleting) return;
    setDeleteTarget(null);
  }

  async function handleConfirmDelete() {
    if (!deleteTarget) return;

    setDeleting(true);
    setDeleteError(null);

    const { error } = await deleteCompany(deleteTarget.id);

    if (error) {
      setDeleteError(L("Failed to delete company.", "Unternehmen konnte nicht gelöscht werden."));
      setDeleting(false);
      return;
    }

    setCompanies((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    setDeleting(false);
    setDeleteTarget(null);
  }

  function startEditDept(companyId: string, name: string) {
    setEditingDept({ companyId, oldName: name });
    setEditingDeptValue(name);
    setDeptError(null);
  }

  function cancelEditDept() {
    if (savingDept) return;
    setEditingDept(null);
  }

  async function handleSaveDeptRename() {
    if (!editingDept) return;

    const newName = editingDeptValue.trim();
    if (!newName) return;

    if (newName === editingDept.oldName) {
      setEditingDept(null);
      return;
    }

    setSavingDept(true);
    setDeptError(null);

    const { error } = await renameCompanyDepartment(editingDept.companyId, editingDept.oldName, newName);

    if (error) {
      setDeptError(L("Failed to rename department.", "Abteilung konnte nicht umbenannt werden."));
      setSavingDept(false);
      return;
    }

    const { companyId, oldName } = editingDept;
    applyDepartmentRename(companyId, oldName, newName);

    setSavingDept(false);
    setEditingDept(null);
  }

  function applyDepartmentRename(companyId: string, oldName: string, newName: string) {
    setDepartmentsByCompany((prev) => {
      const current = prev[companyId] ?? [];
      const next = Array.from(new Set(current.filter((d) => d !== oldName).concat(newName))).sort((a, b) =>
        a.localeCompare(b)
      );
      return { ...prev, [companyId]: next };
    });

    setDepartmentCounts((prev) => {
      const current = { ...(prev[companyId] ?? {}) };
      const moved = current[oldName] ?? 0;
      delete current[oldName];
      current[newName] = (current[newName] ?? 0) + moved;
      return { ...prev, [companyId]: current };
    });
  }

  function openDeleteDeptDialog(companyId: string, name: string) {
    setDeleteDeptTarget({ companyId, name, count: departmentCounts[companyId]?.[name] ?? 0 });
    setDeleteDeptMoveTo("");
    setDeleteDeptError(null);
  }

  function closeDeleteDeptDialog() {
    if (deleteDeptSaving) return;
    setDeleteDeptTarget(null);
  }

  async function handleConfirmDeleteDept() {
    if (!deleteDeptTarget) return;

    const target = deleteDeptMoveTo.trim();
    if (!target || target === deleteDeptTarget.name) return;

    setDeleteDeptSaving(true);
    setDeleteDeptError(null);

    const { error } = await renameCompanyDepartment(deleteDeptTarget.companyId, deleteDeptTarget.name, target);

    if (error) {
      setDeleteDeptError(L("Failed to move employees.", "Mitarbeitende konnten nicht verschoben werden."));
      setDeleteDeptSaving(false);
      return;
    }

    applyDepartmentRename(deleteDeptTarget.companyId, deleteDeptTarget.name, target);

    setDeleteDeptSaving(false);
    setDeleteDeptTarget(null);
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <SectionIconBox>
              <ApartmentOutlinedIcon />
            </SectionIconBox>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.1 }}>
                {L("Companies", "Unternehmen")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {L(
                  "Add, rename or remove companies. Renaming or deleting a department updates every employee currently assigned to it.",
                  "Unternehmen hinzufügen, umbenennen oder entfernen. Das Umbenennen oder Löschen einer Abteilung aktualisiert alle Mitarbeitenden, die ihr aktuell zugeordnet sind."
                )}
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={openAddDialog}
            sx={{ borderRadius: 999, fontWeight: 900, textTransform: "none", px: 2 }}
          >
            {L("Add company", "Unternehmen hinzufügen")}
          </Button>
        </Stack>

        {uiError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {uiError}
          </Alert>
        ) : null}

        {loading ? (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={96} />
            <Skeleton variant="rounded" height={96} />
            <Skeleton variant="rounded" height={96} />
          </Stack>
        ) : companies.length === 0 ? (
          <Paper
            elevation={0}
            sx={{ borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", p: 3 }}
          >
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              {L("No companies yet.", "Noch keine Unternehmen vorhanden.")}
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {companies.map((company) => {
              const count = employeeCounts[company.id] ?? 0;
              const depts = departmentsByCompany[company.id] ?? [];
              const canDelete = count === 0;

              return (
                <Paper
                  key={company.id}
                  elevation={0}
                  sx={{ borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", p: { xs: 2, md: 2.5 } }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: 2.25,
                          bgcolor: "#F3F4F6",
                          display: "grid",
                          placeItems: "center",
                          color: "#374151",
                          flexShrink: 0,
                        }}
                      >
                        <ApartmentOutlinedIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, color: "#111827" }} noWrap>
                          {company.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6B7280" }}>
                          {count} {L("employees", "Mitarbeitende")}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip
                        title={L(
                          "Hide this company from self-registration suggestions",
                          "Dieses Unternehmen bei der Selbstregistrierung ausblenden"
                        )}
                      >
                        <FormControlLabel
                          sx={{ mr: 0.5 }}
                          control={
                            <Switch
                              size="small"
                              checked={Boolean(company.hidden_from_registration)}
                              disabled={togglingHiddenId === company.id}
                              onChange={() => handleToggleHiddenFromRegistration(company)}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 700 }}>
                              {L("Hidden", "Versteckt")}
                            </Typography>
                          }
                        />
                      </Tooltip>
                      <Tooltip title={L("Rename company", "Unternehmen umbenennen")}>
                        <IconButton size="small" onClick={() => openRenameDialog(company)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          canDelete
                            ? L("Delete company", "Unternehmen löschen")
                            : L(
                                "Remove all employees from this company first",
                                "Entferne zuerst alle Mitarbeitenden aus diesem Unternehmen"
                              )
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!canDelete}
                            onClick={() => openDeleteDialog(company)}
                            sx={{ color: canDelete ? "#B91C1C" : undefined }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #F3F4F6" }}>
                    <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {L("Departments in use", "Verwendete Abteilungen")}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 0.75 }}>
                      {depts.length === 0 ? (
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          {L("None yet", "Noch keine")}
                        </Typography>
                      ) : (
                        depts.map((dept) =>
                          editingDept?.companyId === company.id && editingDept.oldName === dept ? (
                            <Stack key={dept} direction="row" spacing={0.5} alignItems="center">
                              <TextField
                                size="small"
                                autoFocus
                                value={editingDeptValue}
                                onChange={(e) => setEditingDeptValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleSaveDeptRename();
                                  if (e.key === "Escape") cancelEditDept();
                                }}
                                disabled={savingDept}
                                sx={{ width: 160, "& .MuiInputBase-root": { height: 32 } }}
                              />
                              <IconButton size="small" onClick={handleSaveDeptRename} disabled={savingDept}>
                                {savingDept ? <CircularProgress size={16} /> : <CheckRoundedIcon fontSize="small" sx={{ color: "#16A34A" }} />}
                              </IconButton>
                              <IconButton size="small" onClick={cancelEditDept} disabled={savingDept}>
                                <CloseRoundedIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Box
                              key={dept}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                height: 28,
                                pl: 1.25,
                                pr: 0.5,
                                gap: 0.25,
                                borderRadius: 999,
                                bgcolor: "#F3F4F6",
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "#374151" }}>
                                {dept}
                              </Typography>
                              <Tooltip title={L("Rename department", "Abteilung umbenennen")}>
                                <IconButton size="small" onClick={() => startEditDept(company.id, dept)} sx={{ p: 0.4 }}>
                                  <EditOutlinedIcon sx={{ fontSize: 15, color: "#9CA3AF" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={L("Delete department", "Abteilung löschen")}>
                                <IconButton
                                  size="small"
                                  onClick={() => openDeleteDeptDialog(company.id, dept)}
                                  sx={{ p: 0.4 }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 15, color: "#9CA3AF" }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )
                        )
                      )}
                    </Stack>
                    {editingDept?.companyId === company.id && deptError ? (
                      <Typography variant="caption" sx={{ color: "#B91C1C", display: "block", mt: 0.75 }}>
                        {deptError}
                      </Typography>
                    ) : null}
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      <Dialog open={addOpen} onClose={closeAddDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900 }}>{L("Add company", "Unternehmen hinzufügen")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            {addError ? (
              <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                {addError}
              </Alert>
            ) : null}
            <TextField
              autoFocus
              fullWidth
              label={L("Company name", "Firmenname")}
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateCompany();
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeAddDialog} disabled={addSaving}>
            {L("Cancel", "Abbrechen")}
          </Button>
          <Button variant="contained" onClick={handleCreateCompany} disabled={addSaving || !addName.trim()}>
            {addSaving ? <CircularProgress size={18} /> : L("Create", "Erstellen")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!renameTarget} onClose={closeRenameDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900 }}>{L("Rename company", "Unternehmen umbenennen")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            {renameError ? (
              <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                {renameError}
              </Alert>
            ) : null}
            <TextField
              autoFocus
              fullWidth
              label={L("Company name", "Firmenname")}
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRenameCompany();
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeRenameDialog} disabled={renameSaving}>
            {L("Cancel", "Abbrechen")}
          </Button>
          <Button variant="contained" onClick={handleRenameCompany} disabled={renameSaving || !renameName.trim()}>
            {renameSaving ? <CircularProgress size={18} /> : L("Save", "Speichern")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900, color: "#B91C1C" }}>
          {L("Delete company?", "Unternehmen löschen?")}
        </DialogTitle>
        <DialogContent>
          {deleteError ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
              {deleteError}
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ color: "#374151" }}>
            {L(
              `Are you sure you want to permanently delete ${deleteTarget?.name ?? ""}? This cannot be undone.`,
              `Möchtest du ${deleteTarget?.name ?? ""} wirklich endgültig löschen? Dies kann nicht rückgängig gemacht werden.`
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            {L("Cancel", "Abbrechen")}
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} /> : L("Delete", "Löschen")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteDeptTarget} onClose={closeDeleteDeptDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900, color: "#B91C1C" }}>
          {L("Delete department?", "Abteilung löschen?")}
        </DialogTitle>
        <DialogContent>
          {deleteDeptError ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
              {deleteDeptError}
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ color: "#374151", mb: 2 }}>
            {L(
              `"${deleteDeptTarget?.name ?? ""}" has ${deleteDeptTarget?.count ?? 0} employee(s). Choose (or type) a department to move them to before it's deleted.`,
              `"${deleteDeptTarget?.name ?? ""}" hat ${deleteDeptTarget?.count ?? 0} Mitarbeitende. Wähle (oder tippe) eine Abteilung, in die sie verschoben werden sollen, bevor gelöscht wird.`
            )}
          </Typography>
          <Autocomplete
            freeSolo
            options={(deleteDeptTarget ? departmentsByCompany[deleteDeptTarget.companyId] ?? [] : []).filter(
              (d) => d !== deleteDeptTarget?.name
            )}
            inputValue={deleteDeptMoveTo}
            onInputChange={(_, value) => setDeleteDeptMoveTo(value)}
            disabled={deleteDeptSaving}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={L("Move employees to", "Mitarbeitende verschieben nach")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleConfirmDeleteDept();
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDeleteDeptDialog} disabled={deleteDeptSaving}>
            {L("Cancel", "Abbrechen")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteDept}
            disabled={
              deleteDeptSaving || !deleteDeptMoveTo.trim() || deleteDeptMoveTo.trim() === deleteDeptTarget?.name
            }
          >
            {deleteDeptSaving ? (
              <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
            ) : (
              L("Move & delete", "Verschieben & löschen")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
